/**
 * Terrain query hooks — REST: /api/terrain (point) + /api/elevation (polygon).
 * Per spec §1.5.6.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { http } from './http';
import type { ElevationResponse, TerrainResponse } from './types';
import type { DroneLive } from '@/lib/store/drones';

export function useTerrainAt(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: ['terrain', lat?.toFixed(3), lng?.toFixed(3)],
    queryFn: () =>
      http
        .get('api/terrain', { searchParams: { lat: String(lat), lng: String(lng) } })
        .json<TerrainResponse>(),
    enabled: lat != null && lng != null,
    staleTime: 60_000,
  });
}

/**
 * Trigger elevation calc once per selected drone + AGL.
 * Real backend will use the OI polygon; here we synthesize the params.
 */
export function useElevationForDrone(drone: DroneLive) {
  const mut = useMutation({
    mutationFn: (input: { agl_m: number }) =>
      http.post('api/elevation', { json: { polygon: { type: 'Polygon', coordinates: [[]] }, agl_m: input.agl_m } }).json<ElevationResponse>(),
  });
  useEffect(() => {
    if (!mut.data && !mut.isPending) {
      mut.mutate({ agl_m: drone.telemetry.aglM ?? 120 });
    }
  }, [drone.telemetry.aglM, mut]);
  return { data: mut.data, isPending: mut.isPending, isError: mut.isError };
}
